import React, { useState, useEffect, memo } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { Globe, MapPin, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useDataMode } from '../../contexts/DataModeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// ISO 3166-1 alpha-2 → numeric mapping (for world-atlas topojson IDs)
const A2N = {
  AD:"020",AE:"784",AF:"004",AG:"028",AL:"008",AM:"051",AO:"024",AQ:"010",AR:"032",AS:"016",AT:"040",AU:"036",AW:"533",AZ:"031",
  BA:"070",BB:"052",BD:"050",BE:"056",BF:"854",BG:"100",BH:"048",BI:"108",BJ:"204",BM:"060",BN:"096",BO:"068",BR:"076",BS:"044",BT:"064",BW:"072",BY:"112",BZ:"084",
  CA:"124",CD:"180",CF:"140",CG:"178",CH:"756",CI:"384",CL:"152",CM:"120",CN:"156",CO:"170",CR:"188",CU:"192",CV:"132",CY:"196",CZ:"203",
  DE:"276",DJ:"262",DK:"208",DM:"212",DO:"214",DZ:"012",
  EC:"218",EE:"233",EG:"818",EH:"732",ER:"232",ES:"724",ET:"231",
  FI:"246",FJ:"242",FM:"583",FR:"250",
  GA:"266",GB:"826",GD:"308",GE:"268",GH:"288",GI:"292",GL:"304",GM:"270",GN:"324",GQ:"226",GR:"300",GT:"320",GU:"316",GW:"624",GY:"328",
  HK:"344",HN:"340",HR:"191",HT:"332",HU:"348",
  ID:"360",IE:"372",IL:"376",IN:"356",IQ:"368",IR:"364",IS:"352",IT:"380",
  JM:"388",JO:"400",JP:"392",
  KE:"404",KG:"417",KH:"116",KM:"174",KN:"659",KP:"408",KR:"410",KW:"414",KY:"136",KZ:"398",
  LA:"418",LB:"422",LC:"662",LI:"438",LK:"144",LR:"430",LS:"426",LT:"440",LU:"442",LV:"428",LY:"434",
  MA:"504",MC:"492",MD:"498",ME:"499",MG:"450",MK:"807",ML:"466",MM:"104",MN:"496",MO:"446",MR:"478",MT:"470",MU:"480",MV:"462",MW:"454",MX:"484",MY:"458",MZ:"508",
  NA:"516",NE:"562",NG:"566",NI:"558",NL:"528",NO:"578",NP:"524",NZ:"554",
  OM:"512",
  PA:"591",PE:"604",PG:"598",PH:"608",PK:"586",PL:"616",PT:"620",PS:"275",PY:"600",
  QA:"634",
  RO:"642",RS:"688",RU:"643",RW:"646",
  SA:"682",SB:"090",SC:"690",SD:"729",SE:"752",SG:"702",SI:"705",SK:"703",SL:"694",SM:"674",SN:"686",SO:"706",SR:"740",SS:"728",ST:"678",SV:"222",SY:"760",SZ:"748",
  TD:"148",TG:"768",TH:"764",TJ:"762",TL:"626",TM:"795",TN:"788",TO:"776",TR:"792",TT:"780",TV:"798",TW:"158",TZ:"834",
  UA:"804",UG:"800",US:"840",UY:"858",UZ:"860",
  VA:"336",VC:"670",VE:"862",VN:"704",VU:"548",
  WS:"882",
  YE:"887",
  ZA:"710",ZM:"894",ZW:"716",
};

const MOCK_COUNTRIES = [
  { code: 'TR', name: 'Türkiye', cities: ['İstanbul', 'Ankara', 'İzmir'] },
  { code: 'DE', name: 'Almanya', cities: ['Frankfurt', 'Berlin', 'Münih'] },
  { code: 'AE', name: 'BAE', cities: ['Dubai', 'Abu Dhabi'] },
  { code: 'US', name: 'ABD', cities: ['Las Vegas', 'New York', 'Chicago'] },
  { code: 'FR', name: 'Fransa', cities: ['Paris', 'Lyon'] },
];

// Memoized map to prevent unnecessary re-renders
const GeoMap = memo(function GeoMap({ selectedNumerics, homeNumeric, hoveredId, onHover }) {
  return (
    <ComposableMap
      projection="geoNaturalEarth1"
      projectionConfig={{ scale: 145, center: [15, 15] }}
      style={{ width: '100%', height: '100%' }}
    >
      <Geographies geography={GEO_URL}>
        {({ geographies }) =>
          geographies.map(geo => {
            const numId = String(geo.id);
            const isHome = numId === homeNumeric;
            const isSel = !isHome && selectedNumerics.includes(numId);
            const isHov = numId === hoveredId;
            return (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                onMouseEnter={() => (isHome || isSel) && onHover(numId)}
                onMouseLeave={() => onHover(null)}
                fill={
                  isHov
                    ? (isHome ? '#2563eb' : isSel ? '#818cf8' : '#475569')
                    : isHome ? '#1e3a8a'
                    : isSel ? '#4f46e5'
                    : '#2d3748'
                }
                stroke="#1a202c"
                strokeWidth={0.35}
                style={{
                  default: { outline: 'none', transition: 'fill 0.15s ease' },
                  hover: { outline: 'none' },
                  pressed: { outline: 'none' },
                }}
              />
            );
          })
        }
      </Geographies>
    </ComposableMap>
  );
});

export default function GlobalPresenceWidget({ token }) {
  const { user } = useAuth();
  const { isMockMode } = useDataMode();
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);

  const homeCode = (user?.org_memberships?.[0]?.country_code || '').toUpperCase();

  useEffect(() => {
    if (isMockMode) {
      setCountries(MOCK_COUNTRIES);
      setLoading(false);
      return;
    }
    if (!token) { setLoading(false); return; }

    axios.get(`${API}/exhibitor/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const shows = r.data?.profile?.upcoming_shows || [];
        const map = {};
        shows.forEach(s => {
          const code = (s.country_code || '').toUpperCase();
          if (!code) return;
          if (!map[code]) map[code] = { code, name: s.country || code, cities: [] };
          if (s.city && !map[code].cities.includes(s.city)) map[code].cities.push(s.city);
        });
        if (homeCode && !map[homeCode]) {
          map[homeCode] = { code: homeCode, name: homeCode, cities: [] };
        }
        setCountries(Object.values(map));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isMockMode, token, homeCode]);

  if (loading) return <div className="h-72 bg-gray-100 animate-pulse rounded-2xl" />;
  if (!isMockMode && countries.length === 0) return null;

  const homeNumeric = A2N[homeCode] || '';
  const selectedNumerics = countries
    .filter(c => c.code !== homeCode)
    .map(c => A2N[c.code])
    .filter(Boolean);
  const totalCities = countries.reduce((s, c) => s + c.cities.length, 0);

  return (
    <div
      className="rounded-2xl overflow-hidden shadow-md"
      style={{ border: '1px solid rgba(99,102,241,0.2)', background: '#0f172a' }}
      data-testid="global-presence-widget"
    >
      {/* Header */}
      <div className="px-5 pt-4 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-indigo-500/20 flex items-center justify-center">
            <Globe className="h-4 w-4 text-indigo-400" />
          </div>
          <div>
            <span className="font-bold text-white text-sm">Global Varlık</span>
            <span className="text-xs text-slate-500 ml-2">· Sergilediğiniz Pazarlar</span>
          </div>
        </div>
        <button
          onClick={() => {
            localStorage.removeItem('exhibitor_onboarding_done');
            navigate('/app/exhibitor/onboarding');
          }}
          className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-0.5 transition-colors"
          data-testid="global-presence-edit-btn"
        >
          Düzenle <ChevronRight className="h-3 w-3" />
        </button>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'rgba(99,102,241,0.15)' }} />

      {/* Map */}
      <div style={{ height: 230, background: '#0f172a' }}>
        <GeoMap
          selectedNumerics={selectedNumerics}
          homeNumeric={homeNumeric}
          hoveredId={hoveredId}
          onHover={setHoveredId}
        />
      </div>

      {/* Legend + stats */}
      <div
        className="px-5 py-3 flex items-center gap-6 flex-wrap"
        style={{ background: 'rgba(15,23,42,0.95)', borderTop: '1px solid rgba(99,102,241,0.1)' }}
      >
        <div className="flex items-center gap-4 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#1e3a8a', border: '1px solid #2563eb' }} />
            <span className="text-slate-400">Kendi ülkeniz</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-indigo-500 inline-block" />
            <span className="text-slate-400">Seçili pazar</span>
          </span>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs font-bold text-white">{countries.length}</span>
          <span className="text-xs text-slate-500">ülke</span>
          {totalCities > 0 && (
            <>
              <span className="text-slate-700 mx-0.5">·</span>
              <MapPin className="h-3 w-3 text-indigo-400" />
              <span className="text-xs font-bold text-white">{totalCities}</span>
              <span className="text-xs text-slate-500">şehir</span>
            </>
          )}
        </div>
      </div>

      {/* Country chips */}
      <div
        className="px-5 pb-4 pt-2 flex flex-wrap gap-2"
        style={{ background: '#0f172a' }}
      >
        {countries.map(c => {
          const isHome = c.code === homeCode;
          return (
            <span
              key={c.code}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium"
              style={{
                background: isHome ? 'rgba(30,58,138,0.4)' : 'rgba(79,70,229,0.2)',
                border: `1px solid ${isHome ? 'rgba(37,99,235,0.4)' : 'rgba(99,102,241,0.3)'}`,
                color: isHome ? '#93c5fd' : '#a5b4fc',
              }}
            >
              {c.name}
              {c.cities.length > 0 && (
                <span className="opacity-60 flex items-center gap-0.5">
                  <MapPin className="h-2.5 w-2.5" />{c.cities.length}
                </span>
              )}
              {isHome && (
                <span className="opacity-50 text-[10px]">ev</span>
              )}
            </span>
          );
        })}
        {isMockMode && (
          <span className="text-xs text-slate-600 self-center ml-1">— örnek veri</span>
        )}
      </div>
    </div>
  );
}
